/* ===== AMTYP — Site-wide JS ===========================================
   Font tweaks panel, mobile nav, FAQ accordion, misc enhancements.
   Vanilla JS, no framework dependencies.
   ====================================================================== */

(function () {
  'use strict';

  const STORAGE_KEY = 'amtyp.settings';
  const DEFAULTS = { pixel: 'pixelmix', body: 'figtree' };

  const loadSettings = () => {
    try { return Object.assign({}, DEFAULTS, JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')); }
    catch { return { ...DEFAULTS }; }
  };
  const saveSettings = (s) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  };

  let state = loadSettings();

  const applyState = () => {
    document.documentElement.dataset.pixel = state.pixel;
    document.documentElement.dataset.body  = state.body;
    saveSettings(state);
  };
  applyState();

  /* ── Tweaks panel ─────────────────────────────────────────────────── */
  const initTweaks = () => {
    const panel = document.getElementById('tweaks-panel');
    if (!panel) return;

    const refresh = () => {
      panel.querySelectorAll('.tw-opt').forEach(btn => {
        btn.classList.toggle('active', String(state[btn.dataset.k]) === btn.dataset.v);
      });
    };

    panel.addEventListener('click', (e) => {
      const t = e.target;
      if (t.dataset.close !== undefined) { panel.classList.remove('open'); return; }
      if (t.classList.contains('tw-opt')) {
        state[t.dataset.k] = t.dataset.v;
        applyState();
        refresh();
      }
    });

    const trigger = document.getElementById('tweaks-trigger-btn');
    if (trigger) trigger.addEventListener('click', () => panel.classList.toggle('open'));

    refresh();
  };

  /* ── Mobile nav ───────────────────────────────────────────────────── */
  const initMobileNav = () => {
    const hamburger = document.querySelector('.hamburger');
    const mobileNav = document.getElementById('mobile-nav');
    if (!hamburger || !mobileNav) return;
    hamburger.addEventListener('click', () => {
      mobileNav.classList.toggle('is-open');
      hamburger.setAttribute('aria-expanded', mobileNav.classList.contains('is-open'));
    });
    mobileNav.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => mobileNav.classList.remove('is-open'));
    });
  };

  /* ── FAQ accordion ────────────────────────────────────────────────── */
  const initFAQ = () => {
    document.querySelectorAll('.faq-question').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.faq-item');
        const wasOpen = item.classList.contains('is-open');
        // close all others in this FAQ section (items are flat siblings, not nested in categories)
        btn.closest('.section-faq')?.querySelectorAll('.faq-item').forEach(i => i.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });
  };

  /* ── Reviews carousel ─────────────────────────────────────────────────
     Progressive enhancement only: the track is a CSS scroll-snap strip, so
     touch swipe and trackpad scrolling work with JS off. This adds the
     arrows (+ their disabled-at-the-ends state) and mouse drag-to-scroll.
     Listeners are delegated on document (scroll is captured, since it does
     not bubble) so everything keeps working inside markup swapped in by
     initSectionRender() — no sra:updated re-binding needed. ──────────── */
  const initReviews = () => {
    const syncArrows = (track) => {
      const section = track.closest('.section-reviews');
      if (!section) return;
      const max = track.scrollWidth - track.clientWidth;
      section.classList.toggle('is-start', track.scrollLeft <= 1);
      section.classList.toggle('is-end', track.scrollLeft >= max - 1);
      section.querySelectorAll('[data-reviews-scroll]').forEach(btn => {
        const back = Number(btn.dataset.reviewsScroll) < 0;
        btn.disabled = back ? section.classList.contains('is-start')
                            : section.classList.contains('is-end');
      });
    };
    const syncAll = () => document.querySelectorAll('.reviews-track').forEach(syncArrows);

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-reviews-scroll]');
      if (!btn) return;
      const track = btn.closest('.section-reviews')?.querySelector('.reviews-track');
      if (!track) return;
      // behavior:'auto' defers to CSS scroll-behavior, so the
      // prefers-reduced-motion rule still wins.
      track.scrollBy({ left: Number(btn.dataset.reviewsScroll) * track.clientWidth * 0.9, behavior: 'auto' });
    });

    document.addEventListener('scroll', (e) => {
      const track = e.target;
      if (track instanceof Element && track.classList?.contains('reviews-track')) syncArrows(track);
    }, true);

    /* Mouse drag-to-scroll. Touch is left alone — native swipe already has
       momentum and rubber-banding that a JS reimplementation only degrades. */
    let drag = null;

    document.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const track = e.target.closest?.('.reviews-track');
      if (!track || !track.closest('[data-layout="carousel"]')) return;
      drag = { track, startX: e.clientX, startLeft: track.scrollLeft, moved: false };
    });

    document.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < 4) return;   // tolerate a jittery click
        drag.moved = true;
        drag.track.classList.add('is-dragging');
        try { drag.track.setPointerCapture(e.pointerId); } catch {}
      }
      drag.track.scrollLeft = drag.startLeft - dx;
      e.preventDefault();
    });

    let swallowClick = false;

    const endDrag = () => {
      if (!drag) return;
      const { track, moved } = drag;
      drag = null;
      if (!moved) return;
      // dropping .is-dragging restores scroll-snap, settling on the nearest card
      track.classList.remove('is-dragging');
      syncArrows(track);
      // eat the click this drag is about to emit, so a dragged card never
      // activates a link. Cleared on a timer rather than by the click itself:
      // if the pointer came up where no click follows, the flag must still go.
      swallowClick = true;
      setTimeout(() => { swallowClick = false; }, 0);
    };
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    document.addEventListener('click', (e) => {
      if (!swallowClick) return;
      swallowClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    window.addEventListener('resize', syncAll);
    document.addEventListener('sra:updated', syncAll);
    syncAll();
  };

  /* ── Section Rendering API — declarative in-place swaps ───────────────
     Markup contract (no per-section JS needed):
       data-sra                    on <a>/<form>  → opt-in trigger
       data-sra-section="<name>"   on a section   → stable id we control, so the
                                                     same section can be found across
                                                     different page URLs
       data-sra-target="<sel>"     on trigger     → section to update
                                                     (default: closest [data-sra-section])
       data-ref="<name>"           on a node      → independently swappable region
       data-sra-update="replace"   on section     → replace whole section, skip ref diff
     We fetch the full target page and lift out the matching [data-sra-section]
     rather than using Shopify's ?section_id=, because template section ids
     (template--<digits>__<key>) are not stable across different page URLs.
     The swapped-in markup is server-rendered, so active state
     (is-current / link.current) comes back correct for free.
     Falls back to native navigation whenever JS can't help. ──────────── */
  const initSectionRender = () => {
    const touched  = new Set();   // section DOM ids updated via SRA (for popstate)
    const inflight = new Map();   // sectionEl → AbortController

    const sectionOf = (trigger) => {
      const sel = trigger.getAttribute('data-sra-target');
      return sel ? document.querySelector(sel)
                 : trigger.closest('[data-sra-section]');
    };

    // Top-level [data-ref] nodes — those not nested inside another [data-ref].
    const topRefs = (root) => {
      const map = new Map();
      root.querySelectorAll('[data-ref]').forEach((el) => {
        if (el.parentElement && el.parentElement.closest('[data-ref]')) return;
        map.set(el.getAttribute('data-ref'), el);
      });
      return map;
    };

    const reconcile = (liveRoot, newRoot, replace) => {
      const newRefs  = topRefs(newRoot);
      const liveRefs = topRefs(liveRoot);
      if (replace || (!newRefs.size && !liveRefs.size)) {
        liveRoot.innerHTML = newRoot.innerHTML;
        return;
      }
      // Remove live refs absent from the response (spot-remove).
      liveRefs.forEach((node, name) => { if (!newRefs.has(name)) node.remove(); });
      // Replace matched refs in place; insert new ones in source order.
      let anchor = null;
      newRefs.forEach((incoming, name) => {
        const adopted  = document.importNode(incoming, true);
        const existing = liveRefs.get(name);
        if (existing) {
          existing.replaceWith(adopted);
        } else if (anchor) {
          anchor.after(adopted);
        } else {
          liveRoot.appendChild(adopted);
        }
        anchor = adopted;
      });
    };

    const swap = (url, sectionEl, { push = true } = {}) => {
      const name = sectionEl.getAttribute('data-sra-section');

      inflight.get(sectionEl)?.abort();
      const ctrl = new AbortController();
      inflight.set(sectionEl, ctrl);
      sectionEl.setAttribute('aria-busy', 'true');
      sectionEl.setAttribute('data-sra-loading', '');

      return fetch(url, { signal: ctrl.signal })
        .then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newSection = doc.querySelector('[data-sra-section="' + name + '"]');
          if (!newSection) throw new Error('section-not-found');
          const replace = sectionEl.getAttribute('data-sra-update') === 'replace';
          reconcile(sectionEl, newSection, replace);
          touched.add(name);
          if (push) history.pushState({ sra: name }, '', url);
          sectionEl.dispatchEvent(new CustomEvent('sra:updated', { bubbles: true }));
        })
        .catch((err) => { if (err.name !== 'AbortError') window.location.assign(url); })
        .finally(() => {
          if (inflight.get(sectionEl) === ctrl) inflight.delete(sectionEl);
          sectionEl.removeAttribute('aria-busy');
          sectionEl.removeAttribute('data-sra-loading');
        });
    };

    const valid = (sectionEl, target) =>
      sectionEl && sectionEl.hasAttribute('data-sra-section') &&
      target.origin === location.origin;

    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-sra]');
      if (!a || e.defaultPrevented || e.button !== 0 ||
          e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target === '_blank') return;
      const url = new URL(a.href, location.href);
      const sectionEl = sectionOf(a);
      if (!valid(sectionEl, url)) return;
      e.preventDefault();
      swap(url.href, sectionEl);
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-sra]');
      if (!form || (form.method || 'get').toLowerCase() !== 'get') return;
      const sectionEl = sectionOf(form);
      const action = new URL(form.getAttribute('action') || location.href, location.href);
      if (!valid(sectionEl, action)) return;
      new URLSearchParams(new FormData(form)).forEach((v, k) => action.searchParams.set(k, v));
      e.preventDefault();
      swap(action.href, sectionEl);
    });

    // Back/forward restore SRA-updated sections from the new URL.
    window.addEventListener('popstate', () => {
      touched.forEach((name) => {
        const sectionEl = document.querySelector('[data-sra-section="' + name + '"]');
        if (sectionEl) swap(location.href, sectionEl, { push: false });
      });
    });
  };

  /* ── Newsletter form ──────────────────────────────────────────────── */
  const initNewsletter = () => {
    document.querySelectorAll('.newsletter-form').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.textContent = 'Subscribed ✓'; btn.disabled = true; }
      });
    });
  };

  /* ── Contact form: preselect Subject from ?subject= ───────────────────
     Links like /pages/contact?subject=Donate#contact-form arrive from the
     anchored-rows CTAs. Matched on the option's visible text, because that's
     what Shopify emails through as contact[subject]. ─────────────────── */
  const initContactSubject = () => {
    const select = document.getElementById('contact-subject');
    if (!select) return;
    const wanted = new URLSearchParams(location.search).get('subject');
    if (!wanted) return;
    const match = [...select.options].find(
      o => o.value !== '' && o.textContent.trim().toLowerCase() === wanted.trim().toLowerCase()
    );
    if (match) match.selected = true;
  };

  /* ── Password page: signup / password-entry toggle ────────────────── */
  const initPasswordReveal = () => {
    document.querySelectorAll('[data-password-panels]').forEach(panels => {
      const btn = panels.querySelector('[data-password-reveal]');
      const entry = panels.querySelector('[data-panel="entry"]');
      const signup = panels.querySelector('[data-panel="signup"]');
      // Nothing to toggle between: leave the markup exactly as served, so a
      // page with only the password form never hides it.
      if (!btn || !entry || !signup) return;

      const labelOpen = btn.dataset.labelOpen || 'Have a password?';
      const labelClose = btn.dataset.labelClose || 'Close';

      const show = which => {
        panels.dataset.reveal = which;
        const isEntry = which === 'entry';
        btn.setAttribute('aria-expanded', String(isEntry));
        btn.textContent = isEntry ? labelClose : labelOpen;
      };

      // A failed password attempt server-renders this marker; come back with
      // the entry panel open so the error is visible rather than hidden.
      show(entry.querySelector('[data-force-entry]') ? 'entry' : 'signup');
      btn.hidden = false;

      btn.addEventListener('click', () => {
        const opening = panels.dataset.reveal !== 'entry';
        show(opening ? 'entry' : 'signup');
        if (opening) entry.querySelector('input[type="password"]')?.focus();
      });
    });
  };

  /* ── Header group height → CSS variable ──────────────────────────── */
  const syncHeaderHeight = () => {
    const wrapper = document.querySelector('.header-wrapper');
    const bar = document.querySelector('.announcement-bar');
    if (!wrapper) return;
    document.documentElement.style.setProperty('--header-h', wrapper.offsetHeight + 'px');
    document.documentElement.style.setProperty('--announcement-h', bar ? bar.offsetHeight + 'px' : '0px');
  };

  /* ── Boot ─────────────────────────────────────────────────────────── */
  const boot = () => {
    initTweaks();
    initMobileNav();
    initFAQ();
    initReviews();
    initSectionRender();
    initNewsletter();
    initContactSubject();
    initPasswordReveal();
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* ── HeroGrid custom element ──────────────────────────────────────────── */
if (!customElements.get('hero-grid')) {
  const GLYPHS = {
    A: ['01110','10001','10001','11111','10001','10001','10001'],
    M: ['10001','11011','10101','10001','10001','10001','10001'],
    T: ['11111','00100','00100','00100','00100','00100','00100'],
    Y: ['10001','10001','01010','00100','00100','00100','00100'],
    P: ['11110','10001','10001','11110','10000','10000','10000'],
  };

  const GLYPH_W = 5;
  const GLYPH_H = 7;
  const GLYPH_GAP = 1;
  const AMTYP_LETTERS = ['A', 'M', 'T', 'Y', 'P'];
  // Each glyph pixel is drawn as at least a 2x2 block of cells: that 2x2
  // pixel is the client's logo, so it must survive on the coarse mobile grid.
  const MIN_GLYPH_SCALE = 2;
  // Share of the grid width the wordmark may fill.
  const WORDMARK_FILL = 0.85;
  // Wordmark width at scale 1, in cells (glyphs + inter-letter gaps).
  const WORDMARK_BASE_W = AMTYP_LETTERS.length * GLYPH_W
                        + (AMTYP_LETTERS.length - 1) * GLYPH_GAP;

  function accentColor() {
    const vars = ['--accent-r', '--accent-b', '--accent-c', '--accent-y', '--accent-p', '--accent-s'];
    const v = vars[Math.floor(Math.random() * vars.length)];
    return getComputedStyle(document.documentElement).getPropertyValue(v).trim() || '#fff';
  }

  function hgDebounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function hgShuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function hgSeededRand(seed) {
    let s = seed;
    return function () {
      s = (s * 16807 + 0) % 2147483647;
      return (s - 1) / 2147483646;
    };
  }

  class HeroGrid extends HTMLElement {
    // Radius of the cursor trigger area, in cells. 0 = single cell under the
    // pointer; 1 covers a 5-cell plus shape. Bump for a larger trigger point.
    static BRUSH_RADIUS = 1;

    connectedCallback() {
      this._canvas = this.querySelector('.hero-grid__canvas');
      this._imagesEl = this.querySelector('.hero-grid__images');
      this._glowEl = this.querySelector('.hero-grid__glow');
      const section = this.closest('.section-hero') || this.parentElement;
      this._toggleBtn = section.querySelector('.hero-grid__toggle');
      this._toggleLabel = section.querySelector('.hero-grid__toggle-label');
      this._clearBtn = section.querySelector('.hero-grid__clear');
      this._disableBtn = section.querySelector('.hero-grid__disable');
      this._disableLabel = section.querySelector('.hero-grid__disable-label');
      this._sizeInput = section.querySelector('.hero-grid__size-input');
      this._labelFade = this._toggleBtn.dataset.labelFade || 'Fade';
      this._labelPersist = this._toggleBtn.dataset.labelPersist || 'Persist';
      this._labelOn = this._disableBtn?.dataset.labelOn || 'On';
      this._labelOff = this._disableBtn?.dataset.labelOff || 'Off';
      this._disabled = false;
      this._timers = new Map();
      this._rafId = null;
      this._imgRafId = null;
      this._brushRadius = this._sizeInput
        ? parseFloat(this._sizeInput.value)
        : HeroGrid.BRUSH_RADIUS;

      // Cache the author-set column count once. _responsiveCols derives the
      // effective count from this; we must not read it back after overwriting
      // the inline --cols (below), or the base would shrink on every resize.
      this._baseCols = parseInt(getComputedStyle(this).getPropertyValue('--cols').trim(), 10) || 80;

      this._build();

      this._parent = this.parentElement || this;
      // Listen on window, not the section: the fixed .header-wrapper
      // (announcement bar + site header) overlaps the top of the grid but is
      // not a descendant of the section, so section-level events never fire
      // there. _onMove guards to the canvas box so it only paints over the grid.
      window.addEventListener('mousemove', this._onMove);
      window.addEventListener('touchstart', this._onTouch, { passive: true });
      window.addEventListener('touchmove', this._onTouch, { passive: true });
      // Reset the stroke so the next touch doesn't join up with this one.
      window.addEventListener('touchend', this._onLeave, { passive: true });
      window.addEventListener('touchcancel', this._onLeave, { passive: true });
      this._parent.addEventListener('mouseleave', this._onLeave);
      this._toggleBtn.addEventListener('click', this._onToggle);
      this._clearBtn.addEventListener('click', this._onClear);
      this._disableBtn?.addEventListener('click', this._onDisable);
      this._sizeInput?.addEventListener('input', this._onSize);

      this._ro = new ResizeObserver(hgDebounce(() => this._onResize(), 150));
      this._ro.observe(this);
    }

    disconnectedCallback() {
      this._timers.forEach(clearTimeout);
      this._timers.clear();
      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._imgRafId) cancelAnimationFrame(this._imgRafId);
      this._ro?.disconnect();
      window.removeEventListener('mousemove', this._onMove);
      window.removeEventListener('touchstart', this._onTouch);
      window.removeEventListener('touchmove', this._onTouch);
      window.removeEventListener('touchend', this._onLeave);
      window.removeEventListener('touchcancel', this._onLeave);
      this._parent?.removeEventListener('mouseleave', this._onLeave);
      this._toggleBtn?.removeEventListener('click', this._onToggle);
      this._clearBtn?.removeEventListener('click', this._onClear);
      this._disableBtn?.removeEventListener('click', this._onDisable);
      this._sizeInput?.removeEventListener('input', this._onSize);
    }

    _responsiveCols() {
      const base = this._baseCols || 80;
      // Halve the columns on narrow screens so each grid cell is ~2x larger,
      // but never below what the wordmark needs at MIN_GLYPH_SCALE.
      const minCols = Math.ceil((WORDMARK_BASE_W * MIN_GLYPH_SCALE) / WORDMARK_FILL);
      return window.innerWidth < 768 ? Math.max(Math.round(base / 2), minCols) : base;
    }

    _build() {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._imgRafId) cancelAnimationFrame(this._imgRafId);
      this._timers.forEach(clearTimeout);
      this._timers.clear();

      const cols = this._responsiveCols();
      // Keep the CSS grid (grid-template-columns: repeat(var(--cols), 1fr))
      // in lockstep with the column count JS builds and positions against.
      this.style.setProperty('--cols', cols);
      const cellSize = this.offsetWidth / cols;
      const rows = Math.ceil(this.offsetHeight / cellSize);
      const total = cols * rows;

      const frag = document.createDocumentFragment();
      const cells = new Array(total);
      for (let i = 0; i < total; i++) {
        const cell = document.createElement('div');
        cell.className = 'hero-grid__cell';
        frag.appendChild(cell);
        cells[i] = cell;
      }

      this._canvas.innerHTML = '';
      this._canvas.appendChild(frag);
      this._cells = cells;
      this._cols = cols;
      this._rows = rows;
      this._cellSize = cellSize;

      this._placeImages(cols, rows, cellSize);
      this._revealAMTYP(cols, rows, cells);
    }

    _placeImages(cols, rows, cellSize) {
      if (!this._imagesEl) return;
      const scriptEl = this.querySelector('.hero-grid__image-data');
      const srcs = scriptEl ? JSON.parse(scriptEl.textContent) : [];
      if (!srcs.length) return;
      this._imagesEl.innerHTML = '';
      const rand = hgSeededRand(Date.now());
      const w = this.offsetWidth;
      const h = this.offsetHeight;

      // Keep images clear of the corner content: top meta strip and the
      // bottom text / counter band. Confine placement to a central band.
      const bandTop = h * 0.18;
      const bandBottom = h * 0.62;
      const bandH = bandBottom - bandTop;

      // One feature image is noticeably larger; the rest stay small.
      const featureIdx = Math.floor(rand() * srcs.length);

      srcs.forEach((src, idx) => {
        const isFeature = idx === featureIdx;
        const imgWPct = isFeature ? 0.30 + rand() * 0.08 : 0.14 + rand() * 0.07;
        const targetW = Math.round(w * imgWPct);
        // Pick a random position seed up front so layout stays deterministic for
        // this render; the box is sized once we know the image's natural ratio.
        const posRandX = rand();
        const posRandY = rand();
        const img = document.createElement('img');
        img.className = 'hero-grid__image';
        img.draggable = false;

        // Size the box to the image's own aspect ratio (no crop, no distortion).
        // Height is derived from the chosen width; if that overflows the safe
        // band the box is scaled down, preserving the ratio either way.
        const layout = () => {
          const ratio = img.naturalWidth && img.naturalHeight
            ? img.naturalHeight / img.naturalWidth
            : 1;
          let imgW = targetW;
          let imgH = Math.round(imgW * ratio);
          if (imgH > bandH) {
            imgH = Math.round(bandH);
            imgW = Math.round(imgH / ratio);
          }
          const left = Math.round(posRandX * Math.max(0, w - imgW));
          const top = Math.round(bandTop + posRandY * Math.max(0, bandH - imgH));
          img.style.cssText = `left:${left}px;top:${top}px;width:${imgW}px;height:${imgH}px`;
        };

        if (img.complete && img.naturalWidth) {
          layout();
        } else {
          img.addEventListener('load', layout, { once: true });
        }
        img.src = src;
        this._imagesEl.appendChild(img);
      });
    }

    _revealAMTYP(cols, rows, cells) {
      const cellSize = this.offsetWidth / cols;
      // Largest integer scale that keeps the wordmark within WORDMARK_FILL of
      // the grid width, floored at MIN_GLYPH_SCALE (_responsiveCols guarantees
      // enough columns for that floor).
      const GLYPH_SCALE = Math.max(MIN_GLYPH_SCALE, Math.floor((cols * WORDMARK_FILL) / WORDMARK_BASE_W));
      const textWidth = WORDMARK_BASE_W * GLYPH_SCALE;
      const startCol = Math.round((cols - textWidth) / 2);

      // Centre the wordmark in the drawable safe area between hero-top and hero-bottom.
      const section = this.closest('.section-hero');
      const heroTop = section?.querySelector('.hero-top');
      const heroBottom = section?.querySelector('.hero-bottom');
      const safeTopPx = heroTop ? heroTop.offsetTop + heroTop.offsetHeight : 0;
      const safeBottomPx = heroBottom ? heroBottom.offsetTop : this.offsetHeight;
      const safeTopRow = Math.ceil(safeTopPx / cellSize);
      const safeBottomRow = Math.floor(safeBottomPx / cellSize);
      const glyphRows = GLYPH_H * GLYPH_SCALE;
      const startRow = safeTopRow + Math.max(0, Math.round((safeBottomRow - safeTopRow - glyphRows) / 2));

      const litCells = [];

      AMTYP_LETTERS.forEach((letter, letterIndex) => {
        const glyph = GLYPHS[letter];
        const letterStartCol = startCol + letterIndex * (GLYPH_W + GLYPH_GAP) * GLYPH_SCALE;
        for (let row = 0; row < GLYPH_H; row++) {
          for (let col = 0; col < GLYPH_W; col++) {
            if (glyph[row][col] === '1') {
              for (let sr = 0; sr < GLYPH_SCALE; sr++) {
                for (let sc = 0; sc < GLYPH_SCALE; sc++) {
                  const gridRow = startRow + row * GLYPH_SCALE + sr;
                  const gridCol = letterStartCol + col * GLYPH_SCALE + sc;
                  if (gridRow >= 0 && gridRow < rows && gridCol >= 0 && gridCol < cols) {
                    const idx = gridRow * cols + gridCol;
                    litCells.push({ cell: cells[idx] });
                  }
                }
              }
            }
          }
        }
      });

      hgShuffle(litCells);

      let i = 0;
      const step = () => {
        for (let n = 0; n < 4 && i < litCells.length; n++, i++) {
          const { cell } = litCells[i];
          if (cell) {
            cell.classList.remove('is-covered');
            cell.style.setProperty('--cell-color', '#fff');
            cell.classList.add('is-lit', 'is-logo');
          }
        }
        if (i < litCells.length) {
          this._rafId = requestAnimationFrame(step);
        }
      };
      this._rafId = requestAnimationFrame(step);
    }

    _onResize() {
      const cols = this._responsiveCols();
      const cellSize = this.offsetWidth / cols;
      const needed = Math.ceil(this.offsetHeight / cellSize);

      if (cols !== this._cols) {
        this._build();
        return;
      }

      const current = this._cells.length;
      const total = needed * cols;

      if (total > current) {
        const frag = document.createDocumentFragment();
        for (let i = current; i < total; i++) {
          const cell = document.createElement('div');
          cell.className = 'hero-grid__cell';
          frag.appendChild(cell);
          this._cells.push(cell);
        }
        this._canvas.appendChild(frag);
      } else if (total < current) {
        for (let i = current - 1; i >= total; i--) {
          const cell = this._cells[i];
          const timer = this._timers.get(cell);
          if (timer) { clearTimeout(timer); this._timers.delete(cell); }
          cell.remove();
        }
        this._cells.length = total;
      }

      this._rows = needed;
      this._cellSize = cellSize;
    }

    _hoverCell(cell) {
      const isLogo = cell.classList.contains('is-logo');
      cell.classList.remove('is-lit', 'is-covered');
      cell.style.setProperty('--cell-color', accentColor());
      cell.classList.add('is-hovered');

      const existing = this._timers.get(cell);
      if (existing) clearTimeout(existing);

      // Logo cells always return to lit; non-logo cells follow the mode.
      if (this.dataset.mode === 'fade' || isLogo) {
        const id = setTimeout(() => {
          cell.classList.remove('is-hovered');
          if (isLogo) {
            cell.style.setProperty('--cell-color', '#fff');
            cell.classList.add('is-lit');
          }
          this._timers.delete(cell);
        }, 600);
        this._timers.set(cell, id);
      }
    }

    _onMove = (e) => {
      this._paintAt(e.clientX, e.clientY);
    }

    // Touch paints like the mouse. Listeners are passive so a swipe still
    // scrolls the page (the hero is full-height; blocking scroll would trap
    // the user). Touch events rather than pointer events: a touch pointermove
    // is cancelled once scrolling starts, touchmove keeps firing. First finger
    // only, so interpolation never draws a line between two fingers.
    _onTouch = (e) => {
      const t = e.touches[0];
      if (t) this._paintAt(t.clientX, t.clientY);
    }

    _paintAt(clientX, clientY) {
      if (this._disabled || !this._canvas || !this._cells) return;
      const rect = this._canvas.getBoundingClientRect();
      // Only paint when the pointer is over the grid box. This lets the brush
      // work under the fixed header, while ignoring movement elsewhere on the
      // page (e.g. once the hero has scrolled out of view).
      if (
        clientX < rect.left || clientX > rect.right ||
        clientY < rect.top || clientY > rect.bottom
      ) {
        this._lastCol = undefined;
        this._lastRow = undefined;
        return;
      }
      const col = Math.floor((clientX - rect.left) / this._cellSize);
      const row = Math.floor((clientY - rect.top) / this._cellSize);

      const c0 = this._lastCol ?? col;
      const r0 = this._lastRow ?? row;
      this._lastCol = col;
      this._lastRow = row;

      const dc = col - c0, dr = row - r0;
      const steps = Math.max(Math.abs(dc), Math.abs(dr), 1);
      const painted = new Set();
      for (let i = 0; i <= steps; i++) {
        const c = Math.round(c0 + dc * (i / steps));
        const r = Math.round(r0 + dr * (i / steps));
        this._paintBrush(c, r, painted);
      }
    }

    // Light every cell whose centre falls within BRUSH_RADIUS of (col, row).
    _paintBrush(col, row, painted) {
      const radius = this._brushRadius;
      const reach = Math.floor(radius);
      for (let dr = -reach; dr <= reach; dr++) {
        for (let dc = -reach; dc <= reach; dc++) {
          if (Math.hypot(dc, dr) > radius) continue;
          const c = col + dc, r = row + dr;
          if (r < 0 || r >= this._rows || c < 0 || c >= this._cols) continue;
          const idx = r * this._cols + c;
          if (painted.has(idx)) continue;
          painted.add(idx);
          const cell = this._cells[idx];
          if (cell) this._hoverCell(cell);
        }
      }
    }

    _onLeave = () => {
      this._lastCol = undefined;
      this._lastRow = undefined;
    }

    _onSize = (e) => {
      const value = parseFloat(e.target.value);
      if (!Number.isNaN(value)) this._brushRadius = value;
    }

    _onClear = () => {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._imgRafId) cancelAnimationFrame(this._imgRafId);
      this._timers.forEach(clearTimeout);
      this._timers.clear();
      for (const cell of this._cells) {
        cell.classList.remove('is-hovered', 'is-lit', 'is-covered', 'is-logo');
        cell.style.removeProperty('--cell-color');
      }
      this._glowEl.innerHTML = '';
      this._glowEl.classList.remove('is-visible');
      this._placeImages(this._cols, this._rows, this._cellSize);
      this._revealAMTYP(this._cols, this._rows, this._cells);
    }

    _onToggle = () => {
      const isPersist = this.dataset.mode === 'persist';
      this.dataset.mode = isPersist ? 'fade' : 'persist';
      this._toggleBtn.setAttribute('aria-pressed', String(!isPersist));
      this._toggleLabel.textContent = isPersist ? this._labelFade : this._labelPersist;

      if (!isPersist) {
        this._timers.forEach(clearTimeout);
        this._timers.clear();
      }
    }

    _onDisable = () => {
      this._disabled = !this._disabled;
      this._disableBtn.setAttribute('aria-pressed', String(this._disabled));
      if (this._disableLabel) {
        this._disableLabel.textContent = this._disabled ? this._labelOn : this._labelOff;
      }

      // Only stops the hover painting. Clear any in-progress paint,
      // leaving the wordmark lit and the images in place.
      if (this._disabled) {
        this._timers.forEach(clearTimeout);
        this._timers.clear();
        for (const cell of this._cells) {
          cell.classList.remove('is-hovered');
          if (cell.classList.contains('is-logo')) {
            cell.classList.add('is-lit');
            cell.style.setProperty('--cell-color', '#fff');
          } else {
            cell.style.removeProperty('--cell-color');
          }
        }
      }
    }
  }

  customElements.define('hero-grid', HeroGrid);
}

/* ── SiteHeader custom element ────────────────────────────────────────── */
if (!customElements.get('site-header')) {
  function _shShuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  function _shRandomColor() {
    if (Math.random() < 0.5) return '#fff';
    const h = Math.floor(Math.random() * 360);
    const s = Math.floor(60 + Math.random() * 30);
    const l = Math.floor(45 + Math.random() * 20);
    return `hsl(${h},${s}%,${l}%)`;
  }

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      const svg = this.querySelector('.amtyp-svg');
      if (!svg) return;

      this._rects = Array.from(svg.querySelectorAll('rect'));
      // Each logo pixel is 4 consecutive rects (a 2x2 block). Give the block
      // one colour so the split adds texture, not 4x the colour noise.
      this._colors = new Map();
      for (let i = 0; i < this._rects.length; i += 4) {
        const color = _shRandomColor();
        this._rects.slice(i, i + 4).forEach(r => this._colors.set(r, color));
      }
      _shShuffle(this._rects);
      this._revealed = 0;

      const isHomepage = document.body.classList.contains('is-homepage');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (isHomepage && !reduceMotion) {
        this._initScrollReveal();
      } else {
        this._initLoadReveal(reduceMotion);
      }
    }

    disconnectedCallback() {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _setRevealed(n) {
      n = Math.max(0, Math.min(this._rects.length, n));
      if (n === this._revealed) return;
      if (n > this._revealed) {
        for (let i = this._revealed; i < n; i++) {
          this._rects[i].style.fill = this._colors.get(this._rects[i]);
          this._rects[i].style.opacity = '1';
        }
      } else {
        for (let i = n; i < this._revealed; i++) {
          this._rects[i].style.opacity = '';
          this._rects[i].style.fill = '';
        }
      }
      this._revealed = n;
    }

    _initScrollReveal() {
      const heroEl = document.querySelector('hero-grid');
      if (!heroEl) {
        this._initLoadReveal(false);
        return;
      }

      let ticking = false;
      let heroH = 0;

      const measure = () => { heroH = heroEl.offsetHeight; };

      const update = () => {
        ticking = false;
        document.body.classList.toggle('has-scrolled', window.scrollY > 0);
        const t = heroH > 0 ? Math.min(1, Math.max(0, window.scrollY / heroH)) : 1;
        this._setRevealed(Math.round(t * this._rects.length));
      };

      this._onScroll = () => {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      };
      this._onResize = () => { measure(); update(); };

      measure();
      update();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    _initLoadReveal(instant) {
      this.style.setProperty('--header-progress', '1');

      if (instant) {
        this._setRevealed(this._rects.length);
        return;
      }

      // 4 rects per frame: each logo pixel is a 2x2 block, so this keeps the
      // reveal the same length as one pixel per frame.
      let i = 0;
      const step = () => {
        if (i >= this._rects.length) return;
        i += 4;
        this._setRevealed(i);
        this._rafId = requestAnimationFrame(step);
      };
      this._rafId = requestAnimationFrame(step);
    }
  }

  customElements.define('site-header', SiteHeader);
}
